import React, {
  useState,
  createContext,
  useEffect,
  useContext,
  useMemo,
} from "react";
import jsonToFormData from "@ajoelp/json-to-formdata";
import axios from "../../../services/Fetch";

import configuration from "../../../config";
import {
  generateGenericFormValidation,
  getDefaultGenericForm,
} from "./validation";
import moment from "moment";

const API_URL = configuration.API_URL;

const getRouteWithEndpoint = (route) =>
  route.startsWith("http") ? route : `${API_URL}${route}`;
const GenericFormContext = createContext(null);
export const GenericFormProvider = ({ children }) => {
  const [entities, setEntities] = useState({});
  const [loading, setLoading] = useState({});

  const setEntity = (entity, data) =>
    setEntities((entities) => ({ ...entities, [entity]: data }));

  const setEntityLoading = (entity, status) =>
    setLoading((loading) => ({ ...loading, [entity]: status }));
  const isLoading = (e) => loading[e];
  const isAnyEntityLoading = (entities) =>
    !!(entities || Object.keys(loading)).find(isLoading);
  const isViewEntitiesLoading = ({ table, relations }) =>
    isAnyEntityLoading([table, ...Object.keys(relations)]);
  // const [isEditing, setIsEditing] = useState(false);
  // const [errors, setErrors] = useState([]);
  // const addError = e => setErrors(errors => errors.concat(e))
  // const cleanErrors = () => setErrors([])
  const fetchEntity = async ({ table, fetchRoute, fetchMethod }) => {
    let entity = entities[table] || isLoading(table);
    if (entity) return entity;
    const promise = axios(getRouteWithEndpoint(fetchRoute || table), {
      method: fetchMethod || "get",
    }).then(({ data }) => data?.result || []);
    setEntityLoading(table, promise);
    entity = await promise;
    setEntityLoading(table, false);
    setEntity(table, entity);
    return entity;
  };
  const addEntitySuccess = (data, table) => {
    setEntities((entities) => ({
      ...entities,
      [table]: (entities[table] || []).concat(data),
    }));
  };

  const addEntity = async (
    { table, fetchRoute, createRoute, createMethod },
    data
  ) => {
    setEntityLoading(table, true);
    const {
      data: { result },
    } = await axios(getRouteWithEndpoint(createRoute || fetchRoute || table), {
      method: createMethod || "post",
      data,
    }).catch((e) => {
      setEntityLoading(table, false);
      throw e;
    });
    addEntitySuccess(result, table);
    setEntityLoading(table, false);
    return result;
  };

  const editEntitySuccess = (id, data, table) => {
    setEntities((entities) => ({
      ...entities,
      [table]: (entities[table] || []).map((item) =>
        id == item.id ? { ...item, ...data } : item
      ),
    }));
  };

  const editEntity = async (
    { table, fetchRoute, updateRoute, updateMethod },
    id,
    data
  ) => {
    setEntityLoading(table, true);
    const {
      data: { result },
    } = await axios(
      getRouteWithEndpoint(`${updateRoute || fetchRoute || table}/${id}`),
      {
        method: updateMethod || "put",
        data,
      }
    ).catch((e) => {
      setEntityLoading(table, false);
      throw e;
    });
    editEntitySuccess(id, result, table);
    setEntityLoading(table, false);
    return result;
  };

  const removeEntitySuccess = (id, table) => {
    setEntities((entities) => ({
      ...entities,
      [table]: (entities[table] || []).filter((data) => id != data.id),
    }));
  };

  const removeEntity = async (
    { table, fetchRoute, deleteRoute, deleteMethod },
    id
  ) => {
    setEntityLoading(table, true);
    const { data } = await axios(
      getRouteWithEndpoint(`${deleteRoute || fetchRoute || table}/${id}`),
      {
        method: deleteMethod || "delete",
        data: { id },
      }
    ).catch((e) => {
      setEntityLoading(table, false);
      throw e;
    });
    removeEntitySuccess(id, table);
    setEntityLoading(table, false);
    return data;
  };

  const fetchViewData = async ({
    table,
    fetchRoute,
    fetchEndpoint,
    fetchMethod,
    relations,
  }) => {
    const data = await fetchEntity({ table, fetchRoute, fetchMethod });
    await Promise.all(
      Object.keys(relations).map(async (key) => {
        relations[key].data = await fetchEntity({
          ...relations[key],
          table: key,
        });
      })
    );
    return {
      data,
      relations,
    };
  };
  const getViewData = ({ table, relations }) => {
    const data = entities[table] || [];
    Promise.all(
      Object.keys(relations).map(async (key) => {
        relations[key].data = entities[key] || [];
      })
    );
    return {
      data,
      relations,
    };
  };
  return (
    <GenericFormContext.Provider
      value={{
        loading,
        isAnyEntityLoading,
        fetchViewData,
        isViewEntitiesLoading,
        getViewData,
        addEntity,
        editEntity,
        removeEntity,
        entities,
      }}
    >
      {children}
    </GenericFormContext.Provider>
  );
};

export const withGenericFormApi = (Component, cfg) => {
  return (props) => {
    const config = useMemo(() => cfg || props.config, [props.config]);
    const {
      fetchViewData,
      isViewEntitiesLoading,
      getViewData,
      addEntity,
      removeEntity,
      loading,
      entities,
      editEntity,
    } = useContext(GenericFormContext);
    const [isEditing, setIsEditing] = useState(null);
    useEffect(() => {
      fetchViewData(config);
    }, []);

    const isLoading = useMemo(() => isViewEntitiesLoading(config), [loading]);
    const viewData = useMemo(() => getViewData(config), [entities]);
    const initialValues = useMemo(() => {
      const initialValues = getDefaultGenericForm(config);
      // if (isEditing) {
      //   const entityEditing =
      //     viewData.data.find(({ id }) => id == isEditing) || {};
      //   Object.entries(entityEditing).map(([key, value]) => {
      //     initialValues[key] = value;
      //   });
      // }
      // console.log({ ...initialValues });

      if (isEditing) {
        const entityEditing =
          viewData.data.find(({ id }) => id == isEditing) || {};
        initialValues.id = isEditing;
        console.log({ entityEditing });
        config.fields.map(({ key, type }) => {
          initialValues[key] =
            type == "date"
              ? moment(entityEditing[key]).format("yyyy-MM-DD")
              : entityEditing[key];
        });
        return entityEditing;
      }
      // console.log(initialValues);
      return initialValues;
    }, [config, isEditing, viewData]);
    const validationSchema = useMemo(
      () => generateGenericFormValidation(config),
      [config, viewData]
    );
    const onSubmit = async (
      { tableData, created_at, updated_at, ...values },
      { setErrors, setStatus, setSubmitting, resetForm }
    ) => {
      // delete values.created_at;
      // delete values.updated_at;
      // delete values.tableData;
      values = jsonToFormData(values);

      console.log(Array.from(values.entries()));
      try {
        if (isEditing) await editEntity(config, isEditing, values);
        else await addEntity(config, values);
        setIsEditing(null);
        resetForm();
      } catch (error) {
        const message = error.message || "Something went wrong";
        setStatus({ success: false });
        setErrors({ submit: message });
        setSubmitting(false);
      }
    };
    const remove = (rowData) => {
      const approved = window.confirm(
        `Are you sure to delete ${config.label}:${rowData.id}`
      );
      if (!approved) return;
      return removeEntity(config, rowData.id);
    };
    // (data) => {};
    // const config = useMemo(
    //   () => ({...config, data: viewData.data}),
    //   [config, viewData]
    // );
    return (
      <Component
        {...props}
        initialValues={initialValues}
        validationSchema={validationSchema}
        isLoading={isLoading}
        viewData={viewData}
        onSubmit={onSubmit}
        remove={remove}
        edit={setIsEditing}
        isEditing={isEditing}
      />
    );
  };
};
